import { computed, inject, Injectable, Injector, resource, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { MessageBarService } from '@softline/ui-core';
import { SoftlineObjectRepository } from '../repositories/softline-object.repository';
import { Project } from '../types/object';
import { ResourceFilterService } from './resource-filter.service';

@Injectable({
  providedIn: 'root',
})
export class SoftlineObjectService {
  private readonly injector = inject(Injector);
  private readonly repository = inject(SoftlineObjectRepository);
  private readonly resourceFilterService = inject(ResourceFilterService);
  public readonly messageBarService = inject(MessageBarService);
  public readonly copyToClipboard = (objectNumber: number) => {
    this.messageBarService.info(
      `Objektnummer ${objectNumber} in die Zwischenablage kopiert.`
    );
    navigator.clipboard.writeText(objectNumber.toString());
  };

  readonly objectsFilter = signal<string>('AL');

  sortDirectionAsc = this.resourceFilterService.sortAscending;

  readonly searchTerm = signal<string | null>(null);

  searchObjects = (term: string | undefined | null) => {
    if (!term) {
      this.searchTerm.set(null);
      return;
    }
    this.searchTerm.set(term);
  };

  objectsResource = resource({
    params: () => ({
      costCenterIds: this.resourceFilterService.selectedCostCenterIds(),
      projectGroupType: '!W,R,I,E',
      state: '!E',
      sectionFromCompany: true,
    }),
    loader: async ({ params }) => {
      if (!params.costCenterIds) {
        return [];
      }
      try {
        return await this.repository.getObjectsForCostCenter({
          projectGroupType: params.projectGroupType,
          state: params.state,
          sectionFromCompany: params.sectionFromCompany,
          costCenterIds: params.costCenterIds.split(',').map((id) => Number(id)),
        });
      } catch (error) {
        handleRequestError(error, this.injector, { show: false });
        return [];
      }
    },
  });

  readonly objectsLoading = computed<boolean>(() => this.objectsResource.isLoading());

  readonly objects = computed<Project[]>(() => {
    const sortDirectionAsc = this.sortDirectionAsc();
    const searchTerm = this.searchTerm()?.toLowerCase() ?? null;
    const objects =
      this.objectsFilter() === 'AL'
        ? this.objectsResource.value() || []
        : this.objectsFilter() === 'LEADER'
          ? this.objectsForLeaderResource.value() || []
          : this.objectsForParticipantResource.value() || [];
    objects.sort((a, b) => {
      const keyA = Number(a.key);
      const keyB = Number(b.key);

      if (keyA < keyB) {
        return sortDirectionAsc ? -1 : 1;
      }
      if (keyA > keyB) {
        return sortDirectionAsc ? 1 : -1;
      }
      return 0;
    });
    if (!searchTerm) {
      return objects;
    }
    return objects.filter(
      (obj) =>
        obj.name.toLowerCase().includes(searchTerm) ||
        obj.key.toString().includes(searchTerm)
    );
  });

  objectsForLeaderResource = resource({
    params: () => ({
      employeeIds: this.resourceFilterService.selectedEmployeeIds(),
      projectGroupType: '!W,R,I,E',
      state: '!E',
      sectionFromCompany: true,
    }),
    loader: async ({ params }) => {
      if (!params.employeeIds) {
        return [];
      }
      try {
        return await this.repository.getObjectsForLeader({
          employeeIds: params.employeeIds.split(',').map((id) => Number(id)),
          projectGroupType: params.projectGroupType,
          state: params.state,
          sectionFromCompany: params.sectionFromCompany,
        });
      } catch (error) {
        handleRequestError(error, this.injector, { show: false });
        return [];
      }
    },
  });

  refreshAll = () => {
    this.objectsResource.reload();
    this.objectsForLeaderResource.reload();
    this.objectsForParticipantResource.reload();
  };

  getObjectById = async (objectId: string): Promise<Project> => {
    return this.repository.getObject(objectId);
  };

  objectsForParticipantResource = resource({
    params: () => ({
      employeeIds: this.resourceFilterService.selectedEmployeeIds(),
      projectGroupType: '!W,R,I,E',
      state: '!E',
      sectionFromCompany: true,
    }),
    loader: async ({ params }) => {
      if (!params.employeeIds) {
        return [];
      }
      try {
        return await this.repository.getObjectsForParticipant({
          employeeIds: params.employeeIds.split(',').map((id) => Number(id)),
          projectGroupType: params.projectGroupType,
          state: params.state,
          sectionFromCompany: params.sectionFromCompany,
        });
      } catch (error) {
        handleRequestError(error, this.injector, { show: false });
        return [];
      }
    },
  });
}
